<section class="agent-runs" aria-label="Agent runs">
  @if (loading()) {
    <div class="agent-runs__status" role="status" aria-live="polite">
      <p class="agent-runs__status-message">Loading agent runs…</p>
    </div>
  } @else if (error()) {
    <div class="agent-runs__status" role="alert">
      <p class="agent-runs__status-message">We couldn't load the agent runs.</p>
      <button type="button" class="agent-runs__retry" (click)="loadRuns()">Retry</button>
    </div>
  } @else {
    <!-- KPI strip -->
    <div class="kpis">
      <div class="kpi">
        <span class="kpi__label">Total Runs</span>
        <span class="kpi__value">{{ kpis().total }}</span>
        <span class="kpi__sub">all runs</span>
      </div>
      <div class="kpi kpi--succeeded">
        <span class="kpi__label">Succeeded</span>
        <span class="kpi__value">{{ kpis().succeeded }}</span>
        <span class="kpi__sub">{{ kpis().succRate }}% success rate</span>
      </div>
      <div class="kpi kpi--failed">
        <span class="kpi__label">Failed / Timed Out</span>
        <span class="kpi__value">{{ kpis().failed }}</span>
        <span class="kpi__sub">needs attention</span>
      </div>
      <div class="kpi kpi--running">
        <span class="kpi__label">In-flight</span>
        <span class="kpi__value">{{ kpis().inflight }}</span>
        <span class="kpi__sub">pending or running</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Reruns</span>
        <span class="kpi__value">{{ kpis().reruns }}</span>
        <span class="kpi__sub">attempt &gt; 1</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Avg Run Time</span>
        <span class="kpi__value">{{ kpis().avg }}</span>
        <span class="kpi__sub">agent execution</span>
      </div>
    </div>

    <!-- filter bar -->
    <div class="filterbar">
      <div class="filtergroup">
        <span class="filtergroup__label">Status</span>
        <div class="chips">
          @for (chip of statusChips; track chip.val) {
            <button
              type="button"
              class="chip"
              [class.chip--active]="statusFilter() === chip.val"
              (click)="setStatusFilter(chip.val)">
              {{ chip.label }}
            </button>
          }
        </div>
      </div>

      <div class="filtergroup">
        <span class="filtergroup__label">Agent</span>
        <div class="dropdown" #agentDropdown [class.is-open]="agentDropdownOpen()">
          <button type="button" class="dropdown__trigger" (click)="toggleAgentDropdown()">
            <span class="dropdown__value" [class.dropdown__value--all]="agentFilter() === 'all'">{{ agentTriggerLabel() }}</span>
            <svg class="dropdown__arrow" viewBox="0 0 10 6" fill="none" aria-hidden="true">
              <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div class="dropdown__panel">
            <input
              #agentSearch
              class="dropdown__search"
              type="search"
              placeholder="Search…"
              [value]="agentQuery()"
              (input)="onAgentQuery(agentSearch.value)" />
            <div class="dropdown__count">{{ agentOptions().length }} {{ agentOptions().length === 1 ? 'option' : 'options' }}</div>
            <div class="dropdown__list">
              @for (opt of agentOptions(); track opt.val) {
                <button
                  type="button"
                  class="dropdown__opt"
                  [class.dropdown__opt--active]="opt.val === agentFilter()"
                  (click)="selectAgent(opt.val)">
                  <span>{{ opt.label }}</span>
                  <svg class="dropdown__opt-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </button>
              } @empty {
                <div class="dropdown__empty">No matches</div>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="filtergroup">
        <span class="filtergroup__label">Run For</span>
        <app-agent-runs-date-range (rangeChange)="onRunForRange($event)" />
      </div>

      <div class="filtergroup">
        <span class="filtergroup__label">Completed (WAT)</span>
        <app-agent-runs-date-range (rangeChange)="onCompletedRange($event)" />
      </div>

      <input
        #runSearch
        class="filterbar__search"
        type="search"
        placeholder="Search agent, context, summary…"
        [value]="search()"
        (input)="onSearch(runSearch.value)" />
    </div>

    <!-- run-history table -->
    <div class="tablewrap">
      <div class="tablescroll">
        <table class="runs-table">
          <colgroup>
            <col class="col-status" />
            <col class="col-runid" />
            <col class="col-attempt" />
            <col class="col-outcome" />
            <col class="col-runfor" />
            <col class="col-handoff" />
            <col class="col-completed" />
          </colgroup>
          <thead>
            <tr>
              <th>Status</th>
              <th>Run #</th>
              <th>Attempt</th>
              <th>Outcome</th>
              <th>Run For</th>
              <th>Handoff &rarr; Duration</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            @for (group of groupedRunVms(); track group.groupKey) {
              <tr
                class="group-header"
                [class.is-collapsed]="group.isCollapsed"
                (click)="toggleGroupCollapse(group.groupKey)">
                <td [attr.colspan]="7">
                  <span class="group-header__chevron" aria-hidden="true">
                    <svg viewBox="0 0 10 6" fill="none">
                      <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <strong>{{ group.groupLabel }}</strong>
                  <span class="group-header__ctx">{{ group.groupKey }}</span>
                  @if (group.hasReruns) {
                    <span class="group-header__attempts">{{ group.attemptCount }} attempts</span>
                  }
                </td>
              </tr>
              @for (row of group.rows; track row.run.agentRunId) {
                <tr [class.is-selected]="selectedId() === row.run.agentRunId" (click)="openDrawer(row.run.agentRunId)">
                  <td>
                    <span [class]="'status-badge ' + row.status.cls">
                      <span class="status-badge__dot" [class.status-badge__dot--pulse]="row.status.live"></span>{{ row.status.label }}
                    </span>
                  </td>
                  <td class="cell-id">{{ row.run.agentRunId }}</td>
                  <td>
                    @if (row.isRerun) {
                      <span class="attempt-pill">#{{ row.run.attempt }} rerun</span>
                    } @else {
                      <span class="attempt-pill attempt-pill--first">#{{ row.run.attempt }}</span>
                    }
                  </td>
                  <td class="cell-outcome">
                    @if (row.run.agentOutcome) {
                      <span class="outcome-text">{{ row.run.agentOutcome }}</span>
                    } @else {
                      <span class="outcome-text outcome-text--empty">No outcome recorded for this run (AgentOutcome is NULL).</span>
                    }
                  </td>
                  <td class="cell-date">{{ row.runForDate }}</td>
                  <td>
                    <div class="mini-hop">
                      <span class="mini-bar" [class.mini-bar--failed]="row.miniBarFailed" [class.mini-bar--open]="row.miniBarOpen">
                        @if (row.miniBar) {
                          <span class="mini-bar__seg--bus" [style.width.%]="row.miniBar.bus"></span>
                          <span class="mini-bar__seg--pickup" [style.width.%]="row.miniBar.pickup"></span>
                          <span class="mini-bar__seg--run" [style.width.%]="row.miniBar.run"></span>
                        } @else {
                          <span class="mini-bar__seg--run mini-bar__seg--full"></span>
                        }
                      </span>
                      <span class="mini-dur" [class.mini-dur--open]="row.durationOpen">{{ row.durationText }}</span>
                    </div>
                  </td>
                  <td>
                    @if (row.completedText) {
                      <span class="cell-time">{{ row.completedText }}</span>
                    } @else if (row.completedInProgress) {
                      <span class="cell-time cell-null">in progress…</span>
                    } @else {
                      <span class="cell-time cell-null">—</span>
                    }
                  </td>
                </tr>
              }
            } @empty {
              <tr class="table-empty">
                <td colspan="7">No agent runs match the current filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span><strong>{{ filteredRunsCount() }}</strong> of {{ totalRuns() }} runs</span>
      </div>
    </div>
  }

  <app-agent-run-drawer
    [open]="selectedId() !== null"
    [vm]="selectedVm()"
    [rerunFeedback]="rerunFeedback()"
    (closed)="closeDrawer()"
    (rerunRequested)="rerunSelected()"
    (stopRequested)="stopSelected()" />
</section>
